/**
 * Auth state hook. Subscribes to the Supabase session and, when authed,
 * fetches the caller's tester row. Surfaces:
 *   - loading flags
 *   - the Supabase session (or null)
 *   - the tester profile row (or null if not on the roster)
 *   - the isAdmin flag (driven by VITE_ADMIN_EMAILS)
 *   - signInWithGoogle + signOut actions
 *
 * Why direct Supabase listener instead of React Query for the session:
 *   Auth state is event-driven (signed in / signed out / token refreshed).
 *   The Supabase client emits these events; subscribing is cleaner than
 *   polling. The tester profile fetch IS done via React Query so it
 *   benefits from caching and background refetch.
 */

import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, isAdminEmail } from "../lib/supabase";
import type { Tester } from "../types";

export interface AuthState {
  /** True while the initial session check is in flight. */
  sessionLoading: boolean;
  /** True while the tester profile fetch is in flight (after we have a session). */
  profileLoading: boolean;
  session: Session | null;
  /** The signed-in user's tester row, or null if they're not on the roster. */
  profile: Tester | null;
  /** Whether the signed-in email is on the admin allowlist (client-side check; RLS still enforces). */
  isAdmin: boolean;
  /** Trigger Google OAuth sign-in. Redirects away from the page. */
  signInWithGoogle: () => Promise<void>;
  /** Sign out and clear local cache. */
  signOut: () => Promise<void>;
}

export function useAuth(): AuthState {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  useEffect(() => {
    // Read the existing session synchronously on mount.
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setSessionLoading(false);
    });

    // Subscribe to changes (sign-in, sign-out, token refresh).
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setSessionLoading(false);
    });

    return () => {
      subscription.subscription.unsubscribe();
    };
  }, []);

  const email = session?.user?.email ?? null;

  const profileQuery = useQuery<Tester | null>({
    queryKey: ["tester-profile", email],
    enabled: !!email,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testers")
        .select("*")
        .eq("email", email!)
        .maybeSingle();
      if (error) throw error;
      return (data as Tester | null) ?? null;
    },
    // The roster shouldn't change often during a tester's session;
    // 60s stale time is plenty.
    staleTime: 60_000,
  });

  const signInWithGoogle = async () => {
    const redirectTo = `${window.location.origin}${import.meta.env.BASE_URL || "/"}`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
        queryParams: {
          // Hint Google to default the account picker to teamrwb.org.
          // Supabase Auth will still reject any non-@teamrwb.org email
          // server-side via the domain check.
          hd: "teamrwb.org",
        },
      },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    queryClient.clear();
  };

  return {
    sessionLoading,
    profileLoading: !!email && profileQuery.isLoading,
    session,
    profile: profileQuery.data ?? null,
    isAdmin: isAdminEmail(email),
    signInWithGoogle,
    signOut,
  };
}
